// frontend/src/pages/superadmin/AirportSubscriptionsManager.jsx
// Gérer quels modules/packages sont actifs pour chaque aéroport client
import React, { useState, useEffect } from 'react';
import {
  Building2, CheckCircle, X, Search, ChevronDown, ChevronUp,
  Layers, Save, RefreshCw, AlertCircle, Calendar, DollarSign,
  PlaneTakeoff, Fuel, CalendarDays, Radar, MessageSquare, CalendarCheck,
  Truck, Receipt, ClipboardList, MapPin, GraduationCap,
  ShieldAlert, TrendingUp, Wrench, BarChart3, Warehouse, Globe, Tag, Package
} from 'lucide-react';
import { api } from '../../contexts/authClient';

// ─── Config groupes (identique à PackagesManager) ─────────────────────────────
const MODULE_GROUPS = {
  client: {
    label: 'Client Packages',
    color: '#3b82f6',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    text: 'text-blue-400',
    slugs: ['client_portal', 'fuel_online', 'hangar_lease', 'maintenance', 'premium'],
  },
  core: {
    label: 'Core Modules',
    color: '#f97316',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/30',
    text: 'text-orange-400',
    slugs: ['movement_billing', 'fuel_management', 'staff_scheduling', 'live_traffic_map',
            'client_chat', 'appointments', 'fleet_management', 'invoicing_hangars'],
  },
  operations: {
    label: 'Airport Operations',
    color: '#06b6d4',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/30',
    text: 'text-cyan-400',
    slugs: ['inspections', 'airport_zones', 'recurring_tasks', 'certifications', 'deletion_audit'],
  },
  analytics: {
    label: 'Analytics',
    color: '#a855f7',
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/30',
    text: 'text-purple-400',
    slugs: ['fuel_analytics', 'maint_analytics', 'financial_analytics', 'flight_analytics', 'hangar_analytics'],
  },
};

const MODULE_ICONS = {
  client_portal: Globe, fuel_online: Fuel, hangar_lease: Warehouse,
  maintenance: Wrench, premium: Tag, movement_billing: PlaneTakeoff,
  fuel_management: Fuel, staff_scheduling: CalendarDays, live_traffic_map: Radar,
  client_chat: MessageSquare, appointments: CalendarCheck, fleet_management: Truck,
  invoicing_hangars: Receipt, inspections: ClipboardList, airport_zones: MapPin,
  recurring_tasks: RefreshCw, certifications: GraduationCap, deletion_audit: ShieldAlert,
  fuel_analytics: TrendingUp, maint_analytics: Wrench, financial_analytics: DollarSign,
  flight_analytics: BarChart3, hangar_analytics: Warehouse,
};

const getGroupForSlug = (slug) => {
  for (const [key, group] of Object.entries(MODULE_GROUPS)) {
    if (group.slugs.includes(slug)) return { key, ...group };
  }
  return { key: 'other', label: 'Other', color: '#6b7280', bg: 'bg-gray-500/10', border: 'border-gray-500/30', text: 'text-gray-400' };
};

// ─── Panel aéroport ────────────────────────────────────────────────────────────
const AirportPanel = ({ airport, allPackages }) => {
  const [subscriptions, setSubscriptions] = useState([]);  // [{package_id, is_active, expires_at, price_paid}]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});  // {package_id: bool}

  useEffect(() => {
    if (expanded) loadSubscriptions();
  }, [expanded]);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/superadmin/airports/${airport.id}/packages`);
      setSubscriptions(res.data || []);
      setPendingChanges({});
    } catch (err) {
      console.error('Error loading subscriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  const isActive = (packageId) => {
    if (pendingChanges.hasOwnProperty(packageId)) return pendingChanges[packageId];
    const sub = subscriptions.find(s => s.package_id === packageId);
    return sub?.is_active || false;
  };

  const togglePackage = (packageId) => {
    setPendingChanges(prev => ({
      ...prev,
      [packageId]: !isActive(packageId),
    }));
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  const saveChanges = async () => {
    setSaving(true);
    try {
      await api.put(`/superadmin/airports/${airport.id}/packages`, {
        packages: Object.entries(pendingChanges).map(([package_id, is_active]) => ({
          package_id: parseInt(package_id),
          is_active,
        }))
      });
      await loadSubscriptions();
    } catch (err) {
      alert('Error saving changes');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const activeCount = allPackages.filter(p => isActive(p.id)).length;

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
      {/* Header aéroport */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-700/30 transition flex items-center justify-between"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Building2 className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <p className="text-white font-medium">{airport.name}</p>
            <p className="text-gray-500 text-xs">{airport.subdomain || airport.database_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-xs text-gray-500">Modules actifs</p>
            <p className="text-white font-bold">{activeCount} / {allPackages.length}</p>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full ${airport.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
            {airport.is_active ? 'Active' : 'Inactive'}
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {/* Contenu expandé */}
      {expanded && (
        <div className="border-t border-gray-700 p-4 space-y-5">
          {loading ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
            </div>
          ) : (
            <>
              {/* Groupes de modules */}
              {Object.entries(MODULE_GROUPS).map(([groupKey, group]) => {
                const groupPackages = allPackages.filter(p => group.slugs.includes(p.name));
                if (groupPackages.length === 0) return null;
                const activeInGroup = groupPackages.filter(p => isActive(p.id)).length;

                return (
                  <div key={groupKey}>
                    <div className={`flex items-center justify-between mb-2 pb-2 border-b ${group.border}`}>
                      <div className="flex items-center gap-2">
                        <Layers className="w-3.5 h-3.5" style={{ color: group.color }} />
                        <span className={`text-sm font-medium ${group.text}`}>{group.label}</span>
                      </div>
                      <span className="text-xs text-gray-500">{activeInGroup}/{groupPackages.length}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {groupPackages.map(pkg => {
                        const Icon = MODULE_ICONS[pkg.name] || Package;
                        const active = isActive(pkg.id);
                        const changed = pendingChanges.hasOwnProperty(pkg.id);

                        return (
                          <div
                            key={pkg.id}
                            onClick={() => togglePackage(pkg.id)}
                            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
                              active
                                ? `${group.bg} ${group.border}`
                                : 'bg-gray-900/40 border-gray-700 hover:border-gray-600'
                            } ${changed ? 'ring-1 ring-yellow-500/50' : ''}`}
                          >
                            <div className={`p-1.5 rounded-lg flex-shrink-0 ${active ? group.bg : 'bg-gray-700/50'}`}>
                              <Icon className="w-3.5 h-3.5" style={{ color: active ? group.color : '#6b7280' }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${active ? 'text-white' : 'text-gray-500'}`}>
                                {pkg.display_name}
                              </p>
                              {pkg.price_monthly > 0 && (
                                <p className="text-xs text-gray-600">${pkg.price_monthly}/mo</p>
                              )}
                            </div>
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                              active ? 'bg-green-500' : 'bg-gray-700'
                            }`}>
                              {active
                                ? <CheckCircle className="w-3 h-3 text-white" />
                                : <X className="w-3 h-3 text-gray-500" />
                              }
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Barre de sauvegarde */}
              <div className={`flex items-center justify-between pt-3 border-t border-gray-700 ${!hasPendingChanges ? 'opacity-50' : ''}`}>
                {hasPendingChanges ? (
                  <span className="flex items-center gap-1.5 text-yellow-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {Object.keys(pendingChanges).length} modification(s) non sauvegardée(s)
                  </span>
                ) : (
                  <span className="text-gray-600 text-sm">Aucun changement en attente</span>
                )}
                <div className="flex gap-2">
                  {hasPendingChanges && (
                    <button
                      onClick={() => setPendingChanges({})}
                      className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 text-sm transition"
                    >
                      Reset
                    </button>
                  )}
                  <button
                    onClick={saveChanges}
                    disabled={!hasPendingChanges || saving}
                    className="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 rounded-lg text-white text-sm font-medium disabled:opacity-40 flex items-center gap-2 transition"
                  >
                    {saving
                      ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <Save className="w-3.5 h-3.5" />
                    }
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Composant principal ───────────────────────────────────────────────────────
const AirportSubscriptionsManager = () => {
  const [airports, setAirports] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [airportsRes, packagesRes] = await Promise.all([
        api.get('/superadmin/airports'),
        api.get('/superadmin/packages'),
      ]);
      setAirports(airportsRes.data || []);
      setAllPackages(packagesRes.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = airports.filter(a =>
    !search || a.name?.toLowerCase().includes(search.toLowerCase()) ||
    a.subdomain?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Airport Subscriptions</h1>
          <p className="text-gray-400 text-sm">Activer / désactiver les modules par aéroport</p>
        </div>
        <button onClick={loadData} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 transition">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          placeholder="Rechercher un aéroport..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-gray-800/50 rounded-xl border border-gray-700">
          <p className="text-gray-400 text-xs">Aéroports</p>
          <p className="text-2xl font-bold text-white mt-1">{airports.length}</p>
        </div>
        <div className="p-3 bg-gray-800/50 rounded-xl border border-gray-700">
          <p className="text-gray-400 text-xs">Modules disponibles</p>
          <p className="text-2xl font-bold text-white mt-1">{allPackages.length}</p>
        </div>
      </div>

      {/* Liste aéroports */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
          <Building2 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">Aucun aéroport trouvé</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(airport => (
            <AirportPanel key={airport.id} airport={airport} allPackages={allPackages} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AirportSubscriptionsManager;